/**
 * LogoutConfirmModal Component
 * Renders a step-by-step confirmation modal portal when users initiate session logout.
 * Includes options to remember login credentials or log out directly.
 */

import React from 'react';
import { createPortal } from 'react-dom';

// Fixed overlay styling for top-level modal backdrop
const overlayStyle = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  background: 'rgba(0, 0, 0, 0.78)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 2147483647,
  padding: '24px',
  overflowY: 'auto',
  boxSizing: 'border-box',
  backdropFilter: 'blur(1px)',
};

const cardStyle = {
  width: '100%',
  maxWidth: '620px',
  minHeight: '240px',
  background: '#ffffff',
  borderRadius: '14px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.24)',
  padding: '28px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: 'auto',
  boxSizing: 'border-box',
};

const titleStyle = {
  margin: '0 0 10px 0',
  color: '#1f2937',
  fontSize: '24px',
  fontWeight: '700',
};

const textStyle = {
  margin: '0 0 22px 0',
  color: '#4b5563',
  fontSize: '16px',
  lineHeight: 1.5,
};

const actionRowStyle = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
};

const btnBase = {
  borderRadius: '8px',
  border: '1px solid transparent',
  padding: '12px 16px',
  fontSize: '15px',
  fontWeight: '600',
  cursor: 'pointer',
};

const secondaryBtn = {
  ...btnBase,
  background: '#ffffff',
  borderColor: '#d1d5db',
  color: '#374151',
};

const dangerBtn = {
  ...btnBase,
  background: '#b91c1c',
  borderColor: '#991b1b',
  color: '#ffffff',
};

const primaryBtn = {
  ...btnBase,
  background: '#1e3f32',
  borderColor: '#1e3f32',
  color: '#ffffff',
};

const disabledBtn = {
  opacity: 0.65,
  cursor: 'not-allowed',
};

const LogoutConfirmModal = ({
  isOpen,
  step,
  userEmail,
  isSubmitting,
  onCancel,
  onContinue,
  onSaveAndLogout,
  onLogoutWithoutSaving,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="logout-modal-title">
      <div style={cardStyle}>
        {step === 'confirm' ? (
          <>
            <h3 id="logout-modal-title" style={titleStyle}>Are you sure you want to log out?</h3>
            <p style={textStyle}>You will be signed out of your account.</p>
            <div style={actionRowStyle}>
              <button type="button" style={secondaryBtn} onClick={onCancel} disabled={isSubmitting}>Cancel</button>
              <button type="button" style={dangerBtn} onClick={onContinue} disabled={isSubmitting}>Yes, log out</button>
            </div>
          </>
        ) : (
          <>
            <h3 id="logout-modal-title" style={titleStyle}>Save details for next login?</h3>
            <p style={textStyle}>
              Save your login details to pre-fill the sign-in form next time?
              {userEmail ? ` (${userEmail})` : ''}
            </p>
            <div style={actionRowStyle}>
              <button type="button" style={secondaryBtn} onClick={onCancel} disabled={isSubmitting}>Cancel</button>
              <button
                type="button"
                style={isSubmitting ? { ...primaryBtn, ...disabledBtn } : primaryBtn}
                onClick={onSaveAndLogout}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing out...' : 'Save and log out'}
              </button>
              <button
                type="button"
                style={isSubmitting ? { ...dangerBtn, ...disabledBtn } : dangerBtn}
                onClick={onLogoutWithoutSaving}
                disabled={isSubmitting}
              >
                Log out without saving
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LogoutConfirmModal;
